/*
  Glooty product page helpers (sections/main-product.liquid). No dependencies besides Dawn's
  global.js / pubsub.js / constants.js, which load before this file.

  <glooty-pdp>          one per product section. Keeps [data-g-sync] text and attributes (seal
                        colour, price and colour dot in the Add to bag button, sticky bar) in step
                        with Dawn's variant change event, or with the picked options when the
                        combination has no variant, runs the "pick your size" state and opens the
                        size guide drawer (bottom sheet on phones, side drawer on wider screens):
                        the size guide page's chart when it has one, else the demo chart template
                        (demo mode only).
  <glooty-sticky-atc>   phone add-to-bag bar, visible while the main button is off screen.
  <glooty-gallery-dots> swipe position dots under the phone gallery.
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('glooty-pdp')) {
    class GlootyPdp extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info') || this.parentElement;
        if (!this.root) return;
        this.live = this.querySelector('[data-g-live]');

        // The variant picker marks the Size option with data-g-size-pending while no size was picked
        // (no ?variant= in the URL). From here on the state lives on product-info, so it survives
        // Dawn re-rendering the variant picker.
        if (this.root.querySelector('[data-g-size][data-g-size-pending]')) this.root.classList.add('g-size-pending');
        this.root.classList.add('g-pdp-js');

        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onPaymentClick = this.onPaymentClick.bind(this);
        this.onPrompt = this.promptSize.bind(this);
        this.onIntent = this.onGuideIntent.bind(this);

        this.root.addEventListener('pointerover', this.onIntent);
        this.root.addEventListener('focusin', this.onIntent);
        this.root.addEventListener('click', this.onClick);
        this.root.addEventListener('change', this.onChange);
        this.root.addEventListener('keydown', this.onKeydown);
        this.root.addEventListener('submit', this.onSubmit, true);
        this.root.addEventListener('click', this.onPaymentClick, true);
        this.root.addEventListener('glooty:prompt-size', this.onPrompt);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }
      }

      disconnectedCallback() {
        if (!this.root) return;
        this.root.removeEventListener('pointerover', this.onIntent);
        this.root.removeEventListener('focusin', this.onIntent);
        this.root.removeEventListener('click', this.onClick);
        this.root.removeEventListener('change', this.onChange);
        this.root.removeEventListener('keydown', this.onKeydown);
        this.root.removeEventListener('submit', this.onSubmit, true);
        this.root.removeEventListener('click', this.onPaymentClick, true);
        this.root.removeEventListener('glooty:prompt-size', this.onPrompt);
        if (this.unsubscribe) this.unsubscribe();
      }

      // Dawn's quick-add modal copies the main section id onto the modal's product-info
      // (data-original-section), so the section id of the event cannot tell the two apart. Dawn
      // writes the new id into every form of the section it re-rendered before publishing, so the
      // variant in the event belongs to us only when our own form already carries it. Those forms
      // by id: a complementary products block with quick add has add-to-cart forms of its own.
      ownsVariant(variant) {
        const section = this.root.dataset.section;
        if (!variant || !section) return false;
        const input = this.root.querySelector(
          `#product-form-${section} input[name="id"], #product-form-installment-${section} input[name="id"]`
        );
        return Boolean(input) && String(variant.id) === input.value;
      }

      onVariantChange({ data } = {}) {
        if (!data || !data.html || !this.ownsVariant(data.variant)) return;
        this.sync(data.html);
        this.syncGallery(data.html);
        this.root.dispatchEvent(new CustomEvent('glooty:synced'));
      }

      // A product with photos tagged by colour (media-gallery[data-g-color]) shows only the picked
      // colour's photos. Dawn already swaps the big photos; here the thumbnails (all colours are in
      // the page, the others hidden), the phone dots and the slide counter follow.
      syncGallery(html) {
        const gallery = this.root.querySelector('media-gallery[data-g-color]');
        const fresh = html.querySelector('media-gallery[data-g-color]');
        if (!gallery || !fresh || gallery.dataset.gColor === fresh.dataset.gColor) return;
        const color = fresh.dataset.gColor;
        gallery.dataset.gColor = color;
        gallery.querySelectorAll('.thumbnail-list__item[data-g-color]').forEach((item) => {
          item.classList.toggle('hidden', item.dataset.gColor !== color);
        });
        const dots = gallery.querySelector('glooty-gallery-dots');
        const freshDots = fresh.querySelector('glooty-gallery-dots');
        if (dots && freshDots) dots.innerHTML = freshDots.innerHTML;
        const total = gallery.querySelector('.slider-counter--total');
        const freshTotal = fresh.querySelector('.slider-counter--total');
        if (total && freshTotal) total.textContent = freshTotal.textContent;
        gallery.querySelectorAll('slider-component').forEach((slider) => {
          if (typeof slider.resetPages === 'function') slider.resetPages();
        });
      }

      // Our variant picker. Dawn puts the new one in before the old one, which stays (hidden, same
      // id) for a moment.
      variantSelects() {
        const selects = this.root.querySelectorAll(`#variant-selects-${this.root.dataset.section}`);
        return Array.from(selects).find((element) => element.style.display !== 'none') || null;
      }

      // The values the picker shows, in option order
      pickedOptions() {
        const selects = this.variantSelects();
        if (!selects) return [];
        return Array.from(selects.querySelectorAll('[data-g-kind]')).map((option) => {
          const field = option.querySelector('input:checked') || option.querySelector('select');
          return {
            kind: option.dataset.gKind,
            size: option.hasAttribute('data-g-size'),
            value: field ? field.value : '',
          };
        });
      }

      // Dawn publishes no variantChange for a combination it has no variant for (setUnavailable):
      // it empties input[name="id"] of our forms, fires 'change' and hides its price. The price by
      // the title hides too, and the seal, the sticky bar and the squat test follow the picker; the
      // next real variant brings the server text back.
      onNoVariant() {
        this.root.querySelectorAll('[data-g-sync="title-price"]').forEach((element) => element.classList.add('hidden'));
        const options = this.pickedOptions();
        const color = options.find((option) => option.kind === 'color');
        if (color && color.value) {
          this.root.querySelectorAll('[data-g-sync="ring-text"][data-g-ring-tpl]').forEach((element) => {
            this.fillSealPart(element, element.dataset.gRingTpl.split('{color}').join(color.value));
          });
        }
        this.root.dispatchEvent(
          new CustomEvent('glooty:no-variant', {
            bubbles: true,
            detail: {
              options: options.map((option) => option.value),
              sizeIndex: options.findIndex((option) => option.size),
            },
          })
        );
      }

      // One arc of the round seal (snippets/glooty-badge-ring.liquid): the first phrase of the
      // label on the top arc, the second on the bottom arc, the whole label round the edge. Long
      // phrases get the same squeeze the snippet gives them (textLength over 15 / 20 letters).
      fillSealPart(element, label) {
        const phrases = label.split('·').map((phrase) => phrase.trim()).filter(Boolean);
        const part = element.dataset.gPart;
        let text = phrases.join(' · ');
        let squeeze = null;
        if (part === 'top') {
          text = phrases[0] || '';
          if (text.length > 15) squeeze = '160';
        } else if (part === 'bottom') {
          text = phrases[1] || '';
          if (text.length > 20) squeeze = '186';
        } else if (text) {
          text += ' ·';
        }
        element.textContent = text.toUpperCase();
        if (part === 'full') return;
        if (squeeze) {
          element.setAttribute('textLength', squeeze);
          element.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        } else {
          element.removeAttribute('textLength');
          element.removeAttribute('lengthAdjust');
        }
      }

      // Copy the server-rendered text of every [data-g-sync] element from Dawn's section response
      // (the seal's arcs are matched by their data-g-part too)
      sync(html) {
        this.root.querySelectorAll('[data-g-sync]').forEach((element) => {
          const key = element.dataset.gSync;
          const part = element.dataset.gPart ? `[data-g-part="${element.dataset.gPart}"]` : '';
          const source = html.querySelector(`[data-g-sync="${key}"]${part}`);
          if (!source) return;
          element.innerHTML = source.innerHTML;
          (element.dataset.gSyncAttrs || '')
            .split(' ')
            .filter(Boolean)
            .forEach((name) => {
              const value = source.getAttribute(name);
              if (value === null) element.removeAttribute(name);
              else element.setAttribute(name, value);
            });
        });
      }

      isPending() {
        return this.root.classList.contains('g-size-pending');
      }

      sizeFieldset() {
        return this.root.querySelector('[data-g-size]');
      }

      clearPending() {
        if (!this.isPending()) return;
        this.root.classList.remove('g-size-pending');
        this.root.dispatchEvent(new CustomEvent('glooty:size-chosen', { bubbles: true }));
      }

      onClick(event) {
        if (event.target.closest('[data-g-size] input[type="radio"]')) this.clearPending();

        const guide = event.target.closest('a[data-g-size-guide]');
        if (guide) this.openGuide(event, guide);
      }

      // Start loading the size guide page as soon as the visitor points at or focuses the link
      onGuideIntent(event) {
        const guide = event.target.closest && event.target.closest('a[data-g-size-guide]');
        if (guide) this.loadGuide(guide.getAttribute('href'));
      }

      onChange(event) {
        const target = event.target;
        if (target.closest('[data-g-size]')) this.clearPending();

        if (target.name !== 'id' || target.value || !target.form) return;
        const section = this.root.dataset.section;
        const formId = target.form.getAttribute('id');
        if (formId === `product-form-${section}` || formId === `product-form-installment-${section}`) {
          this.onNoVariant();
        }
      }

      // Space on the size radio that is already checked fires no click or change, and Enter would
      // submit the form: while the size is still to be picked, both pick the focused size.
      onKeydown(event) {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        if (!this.isPending() || !event.target.matches('[data-g-size] input[type="radio"]')) return;
        if (event.key === 'Enter') event.preventDefault();
        this.clearPending();
      }

      onSubmit(event) {
        if (!this.isPending() || !this.sizeFieldset()) return;
        // Only our own form: a complementary products block with quick add has add-to-cart forms
        // too. getAttribute, because form.id is the input named "id".
        const form = event.target;
        if (!form.getAttribute || form.getAttribute('id') !== `product-form-${this.root.dataset.section}`) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptSize();
      }

      onPaymentClick(event) {
        if (!this.isPending() || !this.sizeFieldset()) return;
        if (!event.target.closest('.shopify-payment-button, shopify-accelerated-checkout')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptSize();
      }

      promptSize() {
        const fieldset = this.sizeFieldset();
        if (!fieldset || !this.isPending()) return;

        fieldset.classList.remove('g-opt--alert');
        void fieldset.offsetWidth;
        fieldset.classList.add('g-opt--alert');
        clearTimeout(this.alertTimer);
        this.alertTimer = setTimeout(() => fieldset.classList.remove('g-opt--alert'), 2400);

        fieldset.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        const input =
          fieldset.querySelector('input[type="radio"]:checked') || fieldset.querySelector('input[type="radio"]');
        if (input) input.focus({ preventScroll: true });

        if (this.live) {
          this.live.textContent = '';
          setTimeout(() => {
            this.live.textContent = 'Pick your size first.';
          }, 50);
        }
      }

      // Size guide drawer: a bottom sheet on phones, a side drawer on wider screens (CSS .g-sheet).
      // The dialog itself has no padding, so a click whose target is the dialog is a backdrop click.
      getDialog(title) {
        if (this.dialog && document.body.contains(this.dialog)) return this.dialog;
        const id = `GSheetTitle-${this.root.dataset.section || 'pdp'}`;
        const dialog = document.createElement('dialog');
        dialog.className = 'g-sheet';
        dialog.setAttribute('aria-labelledby', id);
        dialog.innerHTML =
          '<div class="g-sheet__inner">' +
          '<div class="g-sheet__head">' +
          `<h2 class="g-sheet__title" id="${id}"></h2>` +
          '<button type="button" class="g-sheet__close" aria-label="Close">' +
          '<svg class="g-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18"/></svg>' +
          '</button></div><div class="g-sheet__body"></div></div>';
        dialog.querySelector('.g-sheet__title').textContent = title || 'Size guide';
        dialog.querySelector('.g-sheet__close').addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          if (this.lastGuideLink) this.lastGuideLink.focus({ preventScroll: true });
        });
        document.body.appendChild(dialog);
        this.dialog = dialog;
        return dialog;
      }

      // The size guide page's chart, fetched once (also started on hover / focus of the link). Resolves
      // to a fragment with the chart (table, unit note, notes), or null when the page has no chart.
      loadGuide(url) {
        if (!url || url.charAt(0) === '#') return Promise.resolve(null);
        if (this.guidePromise && this.guideUrl === url) return this.guidePromise;
        this.guideUrl = url;
        this.guidePromise = fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const root = doc.querySelector('[data-glooty-size-guide]') || doc.querySelector('main');
            const table = root && root.querySelector('table');
            if (!table) return null;
            const fragment = document.createDocumentFragment();
            const panel = table.closest('.g-sg__panel') || table;
            const extra = Array.from(root.querySelectorAll('.g-sg__notes'));
            [panel, ...extra].forEach((element) => {
              element.querySelectorAll('script, link, style').forEach((node) => node.remove());
              fragment.appendChild(document.importNode(element, true));
            });
            return fragment;
          })
          .catch(() => null);
        return this.guidePromise;
      }

      demoChart() {
        const template = this.querySelector('template[data-g-size-chart]');
        return template ? template.content.cloneNode(true) : null;
      }

      openGuide(event, link) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
        const url = link.getAttribute('href');
        const dialogSupported = typeof HTMLDialogElement === 'function';
        if (!url || !dialogSupported) return;
        if (url.charAt(0) === '#' && !this.querySelector('template[data-g-size-chart]')) return;
        event.preventDefault();

        this.lastGuideLink = link;
        const dialog = this.getDialog(link.textContent.trim());
        const body = dialog.querySelector('.g-sheet__body');
        body.innerHTML = '<p class="g-sheet__loading">Loading…</p>';
        dialog.showModal();
        document.body.classList.add('overflow-hidden');

        this.loadGuide(url).then((chart) => {
          if (!dialog.open) return;
          // the cached fragment stays intact for the next opening
          const content = chart ? chart.cloneNode(true) : this.demoChart();
          if (!content) {
            dialog.close();
            window.location.href = url;
            return;
          }
          body.replaceChildren(content);
          if (chart && url.charAt(0) !== '#') {
            const more = document.createElement('a');
            more.className = 'g-sheet__more';
            more.href = url;
            more.textContent = 'Full size guide';
            body.appendChild(more);
          }
        });
      }
    }
    customElements.define('glooty-pdp', GlootyPdp);
  }

  if (!customElements.get('glooty-sticky-atc')) {
    class GlootyStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal, .quick-add-modal')) {
          this.remove();
          return;
        }
        this.root = this.closest('product-info');
        this.button = this.querySelector('.g-sticky__button');
        this.label = this.querySelector('[data-g-sticky-label]');
        this.mainButton =
          (this.root && this.root.querySelector(`#ProductSubmitButton-${this.dataset.section}`)) ||
          (this.root && this.root.querySelector('.product-form__submit'));
        if (!this.root || !this.button || !this.mainButton) {
          this.hidden = true;
          return;
        }

        document.body.classList.add('g-has-sticky-atc');
        this.button.addEventListener('click', this.onButtonClick.bind(this));
        this.onSizeChosen = () => this.mirror();
        this.onSynced = () => this.fillNoSize();
        this.onNoVariant = (event) => this.fillPicked(event.detail);
        this.root.addEventListener('glooty:size-chosen', this.onSizeChosen);
        this.root.addEventListener('glooty:synced', this.onSynced);
        this.root.addEventListener('glooty:no-variant', this.onNoVariant);
        this.mirror();
        this.fillNoSize();

        this.mutationObserver = new MutationObserver(() => this.mirror());
        this.mutationObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Glooty: also held back while the Buy 2, get 1 free card is on screen, so on load it doesn't cover the card
        this.onScreen = new Map();
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => this.onScreen.set(entry.target, entry.isIntersecting));
          this.classList.toggle('is-visible', ![...this.onScreen.values()].some(Boolean));
        });
        [this.mainButton, this.root.querySelector('.g-b2g1-pdp')]
          .filter(Boolean)
          .forEach((el) => this.intersectionObserver.observe(el));
      }

      disconnectedCallback() {
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this.intersectionObserver) this.intersectionObserver.disconnect();
        if (this.root && this.onSizeChosen) this.root.removeEventListener('glooty:size-chosen', this.onSizeChosen);
        if (this.root && this.onSynced) this.root.removeEventListener('glooty:synced', this.onSynced);
        if (this.root && this.onNoVariant) this.root.removeEventListener('glooty:no-variant', this.onNoVariant);
        if (!document.querySelector('glooty-sticky-atc.g-sticky:not([hidden])')) {
          document.body.classList.remove('g-has-sticky-atc');
        }
      }

      // Selected options without the size ("Dark Cherry · 28\""), shown while no size is picked yet
      fillNoSize() {
        const target = this.querySelector('[data-g-nosize]');
        const json = this.root.querySelector('variant-selects [data-selected-variant]');
        if (!target || !json) return;
        let variant = null;
        try {
          variant = JSON.parse(json.textContent);
        } catch (error) {
          return;
        }
        if (!variant || !Array.isArray(variant.options)) return;
        const inputs = Array.from(this.root.querySelectorAll('variant-selects .product-form__input'));
        const sizeIndex = inputs.findIndex((input) => input.hasAttribute('data-g-size'));
        target.textContent = variant.options.filter((value, index) => index !== sizeIndex).join(' · ');
      }

      // No variant for the picked combination: the picked options, and no price, like Dawn's main
      // price. The button follows the main one ("Unavailable"), or still asks for the size.
      fillPicked({ options, sizeIndex } = {}) {
        if (!Array.isArray(options)) return;
        const all = this.querySelector('.g-sticky__opts--all');
        const noSize = this.querySelector('[data-g-nosize]');
        const price = this.querySelector('[data-g-sticky-price]');
        if (all) all.textContent = options.join(' · ');
        if (noSize) noSize.textContent = options.filter((value, index) => index !== sizeIndex).join(' · ');
        if (price) price.hidden = true;
      }

      mirror() {
        const text = this.mainButton.querySelector('span');
        if (text && this.label) this.label.textContent = text.textContent.trim();
        this.button.toggleAttribute('data-main-disabled', this.mainButton.hasAttribute('disabled'));
        this.button.disabled = this.mainButton.hasAttribute('disabled') && !this.root.classList.contains('g-size-pending');
      }

      onButtonClick() {
        if (this.root.classList.contains('g-size-pending')) {
          this.root.dispatchEvent(new CustomEvent('glooty:prompt-size'));
          return;
        }
        if (this.mainButton.hasAttribute('disabled')) return;
        this.mainButton.click();
      }
    }
    customElements.define('glooty-sticky-atc', GlootyStickyAtc);
  }

  if (!customElements.get('glooty-gallery-dots')) {
    class GlootyGalleryDots extends HTMLElement {
      connectedCallback() {
        this.viewer = this.parentElement && this.parentElement.querySelector('[id^="GalleryViewer"]');
        if (!this.viewer) return;
        // On wide screens the Glooty layout shows the media as a grid, not a slider, so Dawn's
        // slider can report a slide that does not exist; media-gallery.js would throw on it.
        this.onGuard = (event) => {
          if (!event.detail || !event.detail.currentElement) event.stopImmediatePropagation();
        };
        this.viewer.addEventListener('slideChanged', this.onGuard, true);
        this.onSlideChanged = (event) => this.setActive((event.detail && event.detail.currentPage) - 1);
        this.viewer.addEventListener('slideChanged', this.onSlideChanged);
        this.addEventListener('click', (event) => {
          const dot = event.target.closest('.g-gallery-dots__dot');
          if (!dot) return;
          this.goTo(Array.from(this.children).indexOf(dot));
        });
      }

      disconnectedCallback() {
        if (this.viewer && this.onSlideChanged) this.viewer.removeEventListener('slideChanged', this.onSlideChanged);
        if (this.viewer && this.onGuard) this.viewer.removeEventListener('slideChanged', this.onGuard, true);
      }

      slides() {
        return Array.from(this.viewer.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);
      }

      setActive(index) {
        if (Number.isNaN(index) || index < 0) return;
        Array.from(this.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }

      goTo(index) {
        const slide = this.slides()[index];
        const list = this.viewer.querySelector('[id^="Slider-"]');
        if (!slide || !list) return;
        list.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
        this.setActive(index);
      }
    }
    customElements.define('glooty-gallery-dots', GlootyGalleryDots);
  }
})();
